import React, { memo, useState } from 'react'
import { Box, Flex, Text } from '@chakra-ui/core'
import { Check, Copy } from 'lucide-react'
import { ConversationMessage } from './ConversationModal'
import { IPop, RootState } from 'constants/interfaces'
import { useSelector } from 'react-redux'
import { isEqual } from 'lodash'
import useAvatar from 'hooks/useAvatar'
import { isRtlSelector } from 'redux/selectors/general'
import StoreeDoll from 'assets/doll_icon.png'
import { ThinkingDot } from './ThinkingDot'

interface MessageProps {
  message: ConversationMessage
  index: number
  pop?: IPop
  isBotThinking: boolean
}

// ── Markdown block parser ─────────────────────────────────────────────────────

type MessagePart = { type: 'text'; content: string } | { type: 'markdown'; content: string }

function parseMessageParts(text: string): MessagePart[] {
  const parts: MessagePart[] = []
  const regex = /```markdown\n([\s\S]*?)```/g
  let lastIndex = 0
  let match: RegExpExecArray | null

  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push({ type: 'text', content: text.slice(lastIndex, match.index) })
    }
    parts.push({ type: 'markdown', content: match[1] })
    lastIndex = match.index + match[0].length
  }

  if (lastIndex < text.length) {
    parts.push({ type: 'text', content: text.slice(lastIndex) })
  }

  return parts.length > 0 ? parts : [{ type: 'text', content: text }]
}

// ── MarkdownBlock ─────────────────────────────────────────────────────────────

const MarkdownBlock: React.FC<{ content: string }> = ({ content }) => {
  const [copied, setCopied] = useState(false)

  const handleCopy = () => {
    navigator.clipboard.writeText(content).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }

  return (
    <Box mt="8px" mb="4px" borderRadius="10px" overflow="hidden" style={{ border: '1px solid #E2E8F0' }}>
      {/* Header bar */}
      <Flex
        alignItems="center"
        justifyContent="space-between"
        px="12px"
        py="6px"
        bg="#F7F8FA"
        style={{ borderBottom: '1px solid #E2E8F0' }}
      >
        <Text
          fontSize="11px"
          fontWeight="600"
          color="#718096"
          style={{ textTransform: 'uppercase', letterSpacing: '0.5px' }}
        >
          markdown
        </Text>
        <Flex alignItems="center" cursor="pointer" onClick={handleCopy} style={{ gap: '4px' }}>
          {copied ? <Check size={13} color="#38A169" /> : <Copy size={13} color="#718096" />}
          <Text fontSize="11px" color={copied ? '#38A169' : '#718096'} fontWeight="500">
            {copied ? 'Copied' : 'Copy'}
          </Text>
        </Flex>
      </Flex>
      {/* Content */}
      <Box
        bg="#FAFBFC"
        px="14px"
        py="12px"
        style={{
          fontFamily: 'monospace',
          fontSize: '13px',
          lineHeight: '1.6',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          color: '#2D3748',
        }}
      >
        {content}
      </Box>
    </Box>
  )
}

// ── Message ───────────────────────────────────────────────────────────────────

const avatarStyle: React.CSSProperties = {
  width: 48,
  height: 48,
  minWidth: 48,
  minHeight: 48,
  objectFit: 'cover',
  borderRadius: '50%',
  flexShrink: 0,
  filter: 'drop-shadow(0 3px 1px rgba(0, 0, 0, 0.19))',
}

export const Message = memo(({ message, index, pop, isBotThinking }: MessageProps) => {
  const { profile_image_url, avatar_profile_img_url } = pop || {}
  const isRtl = useSelector(isRtlSelector)
  const theme = useSelector((state: RootState) => state.general.theme)
  const uid = useSelector(({ auth: { uid } }: RootState) => uid, isEqual)
  const userAvatar = useAvatar(uid)
  const isUser = message.isUserMessage
  const botAvatar = profile_image_url || avatar_profile_img_url || StoreeDoll

  const getBorderRadius = () => {
    const userRadius = isRtl ? '0 24px 24px 24px' : '24px 0 24px 24px'
    const botRadius = isRtl ? '24px 0px 24px 24px' : '0 24px 24px 24px'
    return isUser ? userRadius : botRadius
  }

  const getMessageBgColor = () => {
    if (isUser) {
      return '#f6f6f6'
    }
    return theme?.backgroundLight
  }

  const shouldShowThinkingDot = !isUser && isBotThinking && (!message.message || message.message.trim() === '')

  const parts = parseMessageParts(message.message || '')
  const hasMarkdown = !isUser && parts.some((p) => p.type === 'markdown')

  return (
    <Flex
      key={index}
      justify={isUser ? 'flex-end' : 'flex-start'}
      mb="18px"
      px="0"
      align="flex-start"
      style={{ gap: '10px' }}
    >
      {!isUser && (
        <Flex align="center" flexShrink={0} style={{ gap: '10px' }}>
          <img src={botAvatar} alt="Bot" width={48} height={48} style={avatarStyle} />
          {shouldShowThinkingDot && <ThinkingDot />}
        </Flex>
      )}

      {!shouldShowThinkingDot && (
        <Box
          maxW={hasMarkdown ? '95%' : '85%'}
          bg={getMessageBgColor()}
          borderRadius={getBorderRadius()}
          px="22px"
          py="18px"
          boxShadow="0 1px 4px rgba(0,0,0,0.04)"
          fontSize="17px"
          color="#222"
          textAlign={isRtl ? 'right' : 'left'}
          fontWeight="400"
          wordBreak="break-word"
          mt="20px"
        >
          {hasMarkdown ? (
            parts.map((part, i) =>
              part.type === 'markdown' ? (
                <MarkdownBlock key={i} content={part.content} />
              ) : part.content.trim() ? (
                <Text key={i} whiteSpace="pre-line" mb="4px">
                  {part.content}
                </Text>
              ) : null
            )
          ) : (
            <Text whiteSpace="pre-line">{message.message}</Text>
          )}
        </Box>
      )}
      {isUser && <img src={userAvatar} alt="User" width={48} height={48} style={avatarStyle} />}
    </Flex>
  )
})

Message.displayName = 'Message'
